import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Booking } from 'swagger/models';
import { Flex } from 'components/primitives/flex';
import Textarea from 'components/ui/Textarea/Textarea';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import Button from 'components/ui/Button/Button';
import SubmitButton from '../../forms/SubmitButton';
import SelectInput from '../../forms/SelectInput';
import { useBreakpoint } from '../../BreakpointProvider';
import { valueEmpty } from '../../../helpers';
import { mediaMSmMax } from '../../../helpers/breakpoints';

interface Props {
  hideModal: () => void;
  resizeIframe: (spacing?: any) => void;
  openEditBookingModal: () => void;
  isCancel?: boolean;
  isFramed: boolean;
  booking: Booking;
  isSubmitting: boolean;
}

const OwnerDeclineCancelContent = ({
  hideModal,
  isCancel = false,
  resizeIframe,
  isFramed,
  booking,
  openEditBookingModal,
  isSubmitting,
}: Props) => {
  const { register, errors, watch } = useFormContext();

  const breakpoints = useBreakpoint();

  const xSmallScreen = breakpoints.xs;

  const editBooking = async () => {
    hideModal();
    openEditBookingModal();
  };

  const reasons = () => [
    { value: 'Accepted booking by accident', label: 'Accepted booking by accident' },
    { value: 'Boat broken', label: 'Boat broken' },
    { value: 'Boat unavailable', label: 'Boat unavailable' },
    { value: 'Capacity issue', label: 'Capacity issue' },
    { value: 'Captain unavailable', label: 'Captain unavailable' },
    { value: 'Family issue', label: 'Family issue' },
    { value: 'Need to change this booking', label: 'Need to change this booking' },
    { value: 'Not enough time to prepare the boat', label: 'Not enough time to prepare the boat' },
    { value: 'Renter changed plans', label: 'Renter changed plans' },
    { value: 'Renter not qualified', label: 'Renter not qualified' },
    { value: 'Unresponsive renter', label: 'Unresponsive renter' },
    { value: 'Weather concerns', label: 'Weather concerns' },
  ];

  return (
    <div className="Grid Grid--withGutter">
      <div className="Grid-cell u-size4of5 u-smMd-sizeFull">
        <div className="u-fsBase u-textBold u-mb1">
          {isCancel ? 'Select reason' : 'Why are you declining this booking'}
        </div>
        <div className="u-mb1">
          <div className="Select u-sizeFull">
            <SelectInput
              name={isCancel ? 'reason_text' : 'category'}
              labelName="Reason"
              options={reasons()}
              searchable={false}
              showError
              data-testid="reason-selected"
              placeholder="Select a reason..."
              capitalOption
              callback={() =>
                setTimeout(() => {
                  if (xSmallScreen && isFramed) resizeIframe(0);
                }, 100)
              }
              onChange={() =>
                setTimeout(() => {
                  resizeIframe();
                }, 100)
              }
            />
          </div>
        </div>
        {(watch('category') === 'Other' || watch('reason_text') === 'Other') && (
          <div className="u-mb1">
            <div className="js-other-reason-textarea">
              <Textarea
                style={{ minHeight: '120px', resize: 'none' }}
                name={isCancel ? 'reason_text_other' : 'reason_text'}
                maxLength="20000"
                hasValue={isCancel ? !valueEmpty(watch('reason_text_other')) : !valueEmpty(watch('reason_text'))}
                placeholder="Enter details (optional)"
                label="Details (optional)"
                hasError={isCancel ? errors.reason_text_other : errors.reason_text}
                bold
                inputRef={register}
              />
            </div>
            <ErrorMessage error={errors.reason_text || errors.reason_text_other} message="Required." />
          </div>
        )}
        <div className="Grid Grid--withGutter">
          <div className="Grid-cell u-smMd-sizeFull">
            <BtnContainer>
              <SubmitButton
                fullWidth
                dataTestId="action-cta"
                disabled={false}
                additionalClasses="Button--yellow  u-sizeFull u-xs-mb1"
                saving={isSubmitting}
              >
                {isCancel ? 'Cancel' : 'Decline'} booking
              </SubmitButton>
              {booking.can_modify && (
                <Button fullWidth text="edit booking" variant="owner" outline onClick={editBooking} />
              )}
            </BtnContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

const BtnContainer = styled(Flex)`
  gap: 20px;

  ${mediaMSmMax} {
    gap: 10px;
    flex-flow: column;
    padding-bottom: 30px;
  }
`;

export default OwnerDeclineCancelContent;
